import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useSpaData } from "../context/SpaDataContext";
import { useBooking } from "../context/BookingContext";
import { Reveal } from "../components/Reveal";
import { imgUrl } from "../lib/spa";
import { Seo } from "../components/Seo";

export default function MassageDetail() {
  const { slug } = useParams();
  const { t, ml } = useLanguage();
  const { massages, therapists, loading } = useSpaData();
  const { openBooking } = useBooking();

  const massage = massages.find((x) => x.slug === slug);
  if (loading) {
    return (
      <div
        data-testid="massage-detail-loading"
        className="flex min-h-screen items-center justify-center bg-background pt-28"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/30 border-t-gold" />
          <p className="text-[10px] uppercase tracking-[0.35em] text-foreground/55">
            {t("common.loading") || "A carregar..."}
          </p>
        </div>
      </div>
    );
  }
  if (!massage)
    return (
      <div
        data-testid="massage-detail-not-found"
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center text-foreground/65"
      >
        <p className="font-display text-2xl text-foreground">
          {t("massage.notFound") || "Massagem não encontrada"}
        </p>
        <Link to="/massagens" className="text-gold underline underline-offset-4">
          {t("massage.back")}
        </Link>
      </div>
    );

  const massageTherapists = Array.isArray(massage.therapists) ? massage.therapists : [];
  const specialists = massageTherapists
    .map((s) => therapists.find((th) => th.slug === s))
    .filter(Boolean);

  const idx = massages.findIndex((x) => x.slug === slug);
  const safeIdx = idx >= 0 ? idx : 0;
  const totalM = massages.length || 1;
  const prevM = massages[(safeIdx - 1 + totalM) % totalM] || massage;
  const nextM = massages[(safeIdx + 1) % totalM] || massage;
  const others = massages.filter((x) => x.slug !== slug).slice(0, 3);

  return (
    <div data-testid="massage-detail-page">
      <Seo
        title={`${ml(massage.name)} | Magnolia Lunar`}
        description={ml(massage.description).slice(0, 160)}
      />
      {/* Banner */}
      <div className="relative h-[55vh] min-h-[400px] overflow-hidden">
        <img src={imgUrl(massage.cover)} alt={ml(massage.name)} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-black/50 to-black/30" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-7xl px-5 pb-12 sm:px-8">
            <Link
              data-testid="massage-back-link"
              to="/massagens"
              className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 transition-colors hover:text-gold"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={1.5} />
              {t("massage.back")}
            </Link>
            <p className="mt-6 text-[10px] uppercase tracking-[0.45em] text-gold">{t("massage.exclusive")}</p>
            <h1 className="mt-3 font-display text-4xl font-light text-white sm:text-5xl lg:text-6xl">
              {ml(massage.name)}
            </h1>
            <span className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/40 px-4 py-2 text-xs uppercase tracking-widest text-white/80 backdrop-blur-sm">
              <Clock className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
              {massage.duration}
            </span>
            {ml(massage.badge) && (
              <span className="ml-3 inline-flex rounded-full bg-gold px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-black">
                {ml(massage.badge)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 pb-24 pt-14 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-3">
          <Reveal className="lg:col-span-2">
            <p className="font-display text-2xl font-light leading-relaxed text-foreground/75 sm:text-3xl">
              {ml(massage.description)}
            </p>
            <button
              data-testid="massage-detail-book-button"
              onClick={() => openBooking({ massageSlug: massage.slug })}
              className="mt-12 rounded-full bg-gold px-10 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(212,175,55,0.3)]"
            >
              {t("massage.bookNow")}
            </button>
          </Reveal>

          <Reveal delay={0.15}>
            <h3 className="text-xs uppercase tracking-[0.3em] text-foreground/65">{t("massage.specialists")}</h3>
            <div className="mt-6 grid grid-cols-3 gap-3">
              {specialists.map((th) => (
                <Link
                  key={th.slug}
                  data-testid={`massage-specialist-${th.slug}`}
                  to={`/terapeutas/${th.slug}`}
                  className="group text-center"
                >
                  <div className="overflow-hidden rounded-xl border border-foreground/10 transition-colors duration-300 group-hover:border-gold/50">
                    <img
                      src={imgUrl(th.profile)}
                      alt={th.name}
                      loading="lazy"
                      className="aspect-[3/4] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <span className="mt-2 block text-sm text-foreground/82 transition-colors duration-300 group-hover:text-gold">
                    {th.name}
                  </span>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Cross navigation */}
        <div className="mt-24">
          <Reveal>
            <h2 className="font-display text-3xl font-light text-foreground sm:text-4xl">{t("explore.otherMassages")}</h2>
          </Reveal>
          <div className="mt-8 grid gap-6 sm:grid-cols-3">
            {others.map((m, i) => (
              <Reveal key={m.slug} delay={i * 0.08}>
                <Link
                  data-testid={`related-massage-${m.slug}`}
                  to={`/massagens/${m.slug}`}
                  className="group block overflow-hidden rounded-2xl border border-foreground/8 bg-card transition-colors duration-300 hover:border-gold/40"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={imgUrl(m.cover)}
                      alt={ml(m.name)}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex items-center justify-between p-5">
                    <span className="font-display text-xl text-foreground">{ml(m.name)}</span>
                    <ArrowRight className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          <div className="mt-14 flex items-center justify-between gap-4 border-t border-border pt-6">
            <Link
              data-testid="massage-prev-link"
              to={`/massagens/${prevM.slug}`}
              className="group flex items-center gap-3 text-foreground/72 transition-colors hover:text-gold"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={1.5} />
              <span>
                <span className="block text-[10px] uppercase tracking-[0.25em] text-foreground/55">{t("explore.previous")}</span>
                <span className="font-display text-lg">{ml(prevM.name)}</span>
              </span>
            </Link>
            <Link
              data-testid="massage-next-link"
              to={`/massagens/${nextM.slug}`}
              className="group flex items-center gap-3 text-right text-foreground/72 transition-colors hover:text-gold"
            >
              <span>
                <span className="block text-[10px] uppercase tracking-[0.25em] text-foreground/55">{t("explore.next")}</span>
                <span className="font-display text-lg">{ml(nextM.name)}</span>
              </span>
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
